import { useTranslation } from 'react-i18next';
import { InfiosAlert, InfiosButton, InfiosIcon } from '@infios/uikit/react';
import type { HaltPayload } from '../../interfaces/chat';
import styles from './HaltPrompt.module.css';

interface HaltPromptProps {
  halt: HaltPayload;
  /** Disabled once the user has answered or while a turn is streaming. */
  disabled?: boolean;
  /** Resume the halted turn by sending the chosen answer text. */
  onRespond: (text: string) => void;
}

/**
 * Renders the affordances for a halted turn. The engine pauses via
 * askUser / presentOptions / confirmAction; we surface clickable choices so the
 * user picks instead of typing. The selected text is sent back as the next
 * message — useChat attaches the stored callbackId to resume the same turn.
 */
export function HaltPrompt({ halt, disabled, onRespond }: HaltPromptProps) {
  const { t } = useTranslation();

  if (halt.type === 'ESCALATION') {
    return (
      <InfiosAlert variant="info" open className={styles.notice}>
        <InfiosIcon slot="icon" name="account" />
        {t('ChatbotUI.Escalated')}
      </InfiosAlert>
    );
  }

  if (halt.type === 'FORM') {
    return (
      <InfiosAlert variant="info" open className={styles.notice}>
        <InfiosIcon slot="icon" name="information-outline" />
        {t('ChatbotUI.FormUnsupported')}
      </InfiosAlert>
    );
  }

  const options = halt.options ?? [];

  if (options.length > 0) {
    return (
      <div className={styles.options}>
        {options.map((opt) => (
          <InfiosButton
            key={opt.id}
            variant="secondary"
            size="compact"
            className={styles.optionBtn}
            disabled={disabled}
            title={opt.description ?? undefined}
            onClick={() => onRespond(opt.label)}
          >
            {opt.label}
          </InfiosButton>
        ))}
      </div>
    );
  }

  if (halt.type === 'CONFIRMATION') {
    return (
      <div className={styles.options}>
        <InfiosButton
          variant="primary"
          size="compact"
          disabled={disabled}
          onClick={() => onRespond(t('ChatbotUI.ConfirmYes'))}
        >
          {t('ChatbotUI.ConfirmYes')}
        </InfiosButton>
        <InfiosButton
          variant="secondary"
          size="compact"
          disabled={disabled}
          onClick={() => onRespond(t('ChatbotUI.ConfirmNo'))}
        >
          {t('ChatbotUI.ConfirmNo')}
        </InfiosButton>
      </div>
    );
  }

  // QUESTION with no options → user answers via the normal input box.
  return null;
}
